// DeliveriesStage — flow dispatch for the /deliveries route. The intake ENTITY
// differs per flow (not just a few fields), so this is a stage shell, not a
// flag-gated form (see docs/COMMODITY-FLOW-STRATEGY.md §2):
// It dispatches on the CAPABILITY (usesForceCooling), not a hard-coded flow
// literal, so ANY force-cool flow (blueberry today, strawberries/other berries
// next — config, not code) gets the berry crate-intake page:
//   force-cool OFF (citrus)  -> the existing Deliveries page, UNCHANGED
//                               (bin + drench pipeline + KF49 empties + Phytclean)
//   force-cool ON  (berries) -> BlueberryDeliveries (crate intake feeding Precool)
//
// Unlike a section-level capability gate, mounting the WRONG whole page during
// the flow-loading flash is costly (the citrus Deliveries page fires its query
// spray and is briefly interactable on a berry tenant), so this shell waits for
// the flow to resolve. With the persisted query cache that wait is only ever
// visible on a true cold first boot.

import { useProductionFlowState } from '@/lib/useProductionFlow'
import { useFlowManifest } from '@/hooks/useCommodityTemplates'
import QueryStateGate from '@/components/QueryStateGate'
import DeliveriesPage from '@/modules/packhouse/Deliveries'
import BlueberryDeliveries from '@/modules/packhouse/BlueberryDeliveries'
import { BrandLoader } from '@/components/BrandLoader'

export default function DeliveriesStage() {
  const { flow, resolved, state } = useProductionFlowState()
  const manifest = useFlowManifest(flow)
  if (!resolved) {
    // Route-level gate, not a bare spinner: `resolved` is also false for
    // error/paused, and a raw spinner here was the idle-wake audit's UI-2
    // route-level forever-spinner. The gate's `empty` (success-no-data can't
    // happen — the queryFn defaults to CITRUS) doubles as the genuine
    // still-resolving spinner while the tenant itself is loading.
    return (
      <div className="p-4 max-w-lg mx-auto">
        <QueryStateGate query={state} noun="this page's setup"
          empty={
            <div className="flex justify-center py-16">
              <BrandLoader className="w-8 h-8" />
            </div>
          } />
      </div>
    )
  }
  return manifest.usesForceCooling ? <BlueberryDeliveries /> : <DeliveriesPage />
}
